import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {{ id: number, o: string[], a: boolean }} Variant
 */

/**
 * Halven House collection page: style chips, client-side sort, color swatches that swap the card photo,
 * and quick add (size sheet) through Horizon's cart events so the drawer opens like on the product page.
 */
class HhCollection extends HTMLElement {
  connectedCallback() {
    this.#initChips();
    // ?style=<chip> preselects a filter, so an ad (or a banner) can land straight on e.g. overcoats
    const style = new URLSearchParams(window.location.search).get('style');
    const chip = style ? this.querySelector(`[data-chip="${CSS.escape(style)}"]`) : null;
    if (chip instanceof HTMLElement && !chip.hidden) this.#filter(chip);
    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-sort]')?.addEventListener('change', this.#onSort);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  /** Shadow under the filter bar once it is stuck to the top of the screen. */
  #onScroll = () => {
    const bar = this.querySelector('.hc-bar');
    if (bar) bar.classList.toggle('stuck', bar.getBoundingClientRect().top <= 0.5);
  };

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('scroll', this.#onScroll);
  }

  /** @returns {HTMLElement[]} */
  get #cards() {
    return Array.from(this.querySelectorAll('[data-grid] > .hc-pc'));
  }

  /* ---------- filters ---------- */

  /** Count matches per chip and reveal only the chips that hold at least 2 products. */
  #initChips() {
    const cards = this.#cards;
    this.querySelectorAll('[data-chips] [data-words]').forEach((chip) => {
      if (!(chip instanceof HTMLElement)) return;
      const n = cards.filter((c) => this.#matches(c, chip)).length;
      const small = chip.querySelector('small');
      if (small) small.textContent = String(n);
      chip.hidden = n < 2;
    });
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLElement} chip
   */
  #matches(card, chip) {
    const words = (chip.dataset.words || '').split('|').map((w) => w.trim()).filter(Boolean);
    const text = card.dataset.words || '';
    return words.some((w) => text.includes(w));
  }

  /** @param {HTMLElement} chip */
  #filter(chip) {
    this.querySelectorAll('[data-chips] button').forEach((b) => b.classList.toggle('on', b === chip));
    const all = chip.dataset.chip === 'all';
    let shown = 0;
    for (const card of this.#cards) {
      const ok = all || this.#matches(card, chip);
      card.hidden = !ok;
      if (ok) shown++;
    }
    // style banners only make sense in the full, unfiltered edit
    this.querySelectorAll('[data-brk]').forEach((b) => b instanceof HTMLElement && (b.hidden = !all));
    const none = /** @type {HTMLElement | null} */ (this.querySelector('[data-none]'));
    if (none) none.hidden = shown > 0;
  }

  /* ---------- sort ---------- */

  #onSort = (/** @type {Event} */ event) => {
    const mode = /** @type {HTMLSelectElement} */ (event.target).value;
    const grid = this.querySelector('[data-grid]');
    if (!grid) return;
    const breaks = Array.from(this.querySelectorAll('[data-brk]'));
    const deal = this.querySelector('[data-deal]');
    const cards = this.#cards.sort((a, b) => {
      if (mode === 'price-asc') return Number(a.dataset.price) - Number(b.dataset.price);
      if (mode === 'price-desc') return Number(b.dataset.price) - Number(a.dataset.price);
      return Number(a.dataset.i) - Number(b.dataset.i);
    });
    cards.forEach((c, i) => {
      grid.append(c);
      if (deal && i === 2) grid.append(deal);
      const b = (i + 1) % 8 === 0 ? breaks[(i + 1) / 8 - 1] : null;
      if (b) grid.append(b);
    });
  };

  /* ---------- clicks ---------- */

  #onClick = (/** @type {MouseEvent} */ event) => {
    const t = /** @type {HTMLElement} */ (event.target);

    const go = t.closest('[data-chip-go]');
    if (go instanceof HTMLElement) {
      const target = this.querySelector(`[data-chips] [data-chip="${CSS.escape(go.dataset.chipGo || '')}"]`);
      if (target instanceof HTMLElement) {
        this.#filter(target);
        target.scrollIntoView({ block: 'nearest', inline: 'center' });
        const bar = this.querySelector('.hc-bar');
        if (bar) window.scrollTo({ top: bar.getBoundingClientRect().top + window.scrollY - 2, behavior: 'smooth' });
      }
      return;
    }

    const chip = t.closest('[data-chips] button');
    if (chip instanceof HTMLElement) return this.#filter(chip);
    if (t.closest('[data-chip-reset]')) {
      const all = this.querySelector('[data-chip="all"]');
      if (all instanceof HTMLElement) this.#filter(all);
      return;
    }

    const sw = t.closest('[data-sw] button');
    if (sw instanceof HTMLElement) return this.#pickColor(sw);

    const qa = t.closest('[data-qa]');
    if (qa instanceof HTMLButtonElement) return void this.#openSheet(qa);

    if (t.closest('[data-sheet-close]')) {
      const sheet = t.closest('[data-sheet]');
      if (sheet instanceof HTMLElement) sheet.hidden = true;
    }
  };

  /** Swatch: swap the card photo to that color, point the links at that variant. @param {HTMLElement} sw */
  #pickColor(sw) {
    const card = sw.closest('.hc-pc');
    if (!(card instanceof HTMLElement)) return;
    card.querySelectorAll('[data-sw] button').forEach((b) => b.classList.toggle('on', b === sw));
    const img = card.querySelector('.hc-i1');
    if (img instanceof HTMLImageElement && sw.dataset.src) {
      img.removeAttribute('srcset');
      img.src = sw.dataset.src;
    }
    card.querySelectorAll('[data-link]').forEach((a) => {
      if (!(a instanceof HTMLAnchorElement) || !sw.dataset.vid) return;
      const url = new URL(a.href, window.location.origin);
      url.searchParams.set('variant', sw.dataset.vid);
      a.href = url.pathname + url.search;
    });
    const sheet = card.querySelector('[data-sheet]');
    if (sheet instanceof HTMLElement && !sheet.hidden) this.#fillSheet(card);
  }

  /* ---------- quick add ---------- */

  /** @param {HTMLElement} card @returns {Variant[]} */
  #variants(card) {
    try {
      return JSON.parse(card.querySelector('[data-v]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  /** @param {HTMLButtonElement} button */
  async #openSheet(button) {
    if (button.classList.contains('ok')) return;
    const card = button.closest('.hc-pc');
    if (!(card instanceof HTMLElement)) return;
    const variants = this.#variants(card);
    if (variants.length === 1 && variants[0]) return this.#add(card, variants[0].id);
    this.querySelectorAll('[data-sheet]').forEach((s) => s instanceof HTMLElement && s !== card.querySelector('[data-sheet]') && (s.hidden = true));
    this.#fillSheet(card);
    const sheet = card.querySelector('[data-sheet]');
    if (sheet instanceof HTMLElement) sheet.hidden = false;
  }

  /** Size buttons for the card's selected color. @param {HTMLElement} card */
  #fillSheet(card) {
    const variants = this.#variants(card);
    const swBox = /** @type {HTMLElement | null} */ (card.querySelector('[data-sw]'));
    const ci = swBox ? Number(swBox.dataset.ci) : -1;
    const on = /** @type {HTMLElement | null} */ (card.querySelector('[data-sw] button.on'));
    const color = ci >= 0 ? on?.dataset.c ?? variants.find((v) => v.a)?.o[ci] : null;
    const pick = variants.filter((v) => ci < 0 || v.o[ci] === color);
    const title = card.querySelector('[data-sheet-title]');
    if (title) title.textContent = color ? `${color} · select size` : 'Select size';
    const opts = card.querySelector('[data-sheet-opts]');
    if (!opts) return;
    opts.replaceChildren(
      ...pick.map((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = v.o.filter((_, i) => i !== ci).join(' / ') || 'Add';
        b.disabled = !v.a;
        b.addEventListener('click', () => void this.#add(card, v.id));
        return b;
      })
    );
  }

  /**
   * Same contract as the product page: resolve Horizon's CartLinesUpdateEvent so the drawer opens.
   * @param {HTMLElement} card
   * @param {number} id
   */
  async #add(card, id) {
    const sheet = card.querySelector('[data-sheet]');
    const qa = card.querySelector('[data-qa]');
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(','), sections_url: window.location.pathname }),
        }),
      }).then((r) => r.json());
      if (response.status) throw new Error(response.description || response.message || 'Could not add to bag');
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'hh-collection', itemCount: 1, sections: response.sections, didError: false },
      });
      if (sheet instanceof HTMLElement) sheet.hidden = true;
      if (qa) {
        qa.classList.add('ok');
        qa.textContent = '✓';
        setTimeout(() => {
          qa.classList.remove('ok');
          qa.textContent = '+';
        }, 2500);
      }
      if (!document.querySelector('cart-drawer-component')) window.location.href = Theme.routes.cart_url;
    } catch (e) {
      deferred.reject(e);
      this.dispatchEvent(new CartErrorEvent({ error: e instanceof Error ? e.message : String(e), code: 'INVALID' }));
    }
  }
}

if (!customElements.get('hh-collection')) customElements.define('hh-collection', HhCollection);
